import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { getApiMessagesUnreadCount } from './api/messages-client';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

interface UnreadMessagesContextType {
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  refreshUnreadCount: (force?: boolean) => Promise<void>;
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | undefined>(undefined);

// Cache lifetime in milliseconds (30 seconds)
const CACHE_LIFETIME = 30 * 1000;

// Component to handle location-based refreshing
const UnreadMessagesUpdater = () => {
  const location = useLocation();
  const { refreshUnreadCount } = useUnreadMessages();
  
  useEffect(() => {
    refreshUnreadCount();
  }, [location.pathname, refreshUnreadCount]);
  
  return null; // This component doesn't render anything
};

export const UnreadMessagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { token } = useAuth();
  
  // Use refs to track last fetch time to avoid excessive API calls
  const lastFetchTimeRef = useRef<number>(0);
  const fetchingRef = useRef<boolean>(false);

  const refreshUnreadCount = async (force = false) => {
    if (!token) {
      setUnreadCount(0);
      return;
    }
    
    const now = Date.now();
    
    // Skip if we're already fetching or if the cache is still valid (unless forced)
    if (fetchingRef.current || (!force && now - lastFetchTimeRef.current < CACHE_LIFETIME)) {
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      const data = await getApiMessagesUnreadCount();
      setUnreadCount(data.unreadCount || 0);
      lastFetchTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      fetchingRef.current = false;
    }
  };

  // Refresh when token changes (login/logout)
  useEffect(() => {
    if (token) {
      refreshUnreadCount(true); // Force refresh on login
    } else {
      setUnreadCount(0);
      lastFetchTimeRef.current = 0;
    }
  }, [token]);

  // Set up periodic refresh (every minute)
  useEffect(() => {
    if (!token) return;
    
    const intervalId = setInterval(() => {
      refreshUnreadCount(true); // Force refresh for periodic updates
    }, 60 * 1000); // Every minute
    
    return () => clearInterval(intervalId);
  }, [token]);

  const value = {
    unreadCount,
    setUnreadCount,
    refreshUnreadCount
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export const useUnreadMessages = (): UnreadMessagesContextType => {
  const context = useContext(UnreadMessagesContext);
  if (context === undefined) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

// Export the updater to be used in the Layout component
export { UnreadMessagesUpdater };